import calculateRank from './calculateRank';

// アニメのステータスを計算する関数
export const getWatchedEpisodeCount = (anime) => {
  if (!anime?.episodes) return 0;

  return anime.episodes.filter(ep => {
    if (anime.ratingMode === 'detailed') {
      const detailed = ep.detailedRating;
      return !!detailed && (
        (detailed.content ?? 0) > 0 ||
        (detailed.animation ?? 0) > 0 ||
        (detailed.sound ?? 0) > 0
      );
    }
    return (ep.rating ?? 0) > 0;
  }).length;
};

export const getAnimeStatus = (anime) => {
  if (anime.dropped) return 'dropped';
  
  const watchedEpisodes = getWatchedEpisodeCount(anime);
  
  if (watchedEpisodes === 0) return 'not-started';
  if (watchedEpisodes === anime.episodes.length) return 'complete';
  return 'watching';
};

// アニメの平均評価を計算する関数
export const getAnimeAverageRating = (anime) => {
  if (anime.ratingMode === 'detailed') {
    const ratedEpisodes = anime.episodes.filter(ep => 
      ep.detailedRating && (ep.detailedRating.content > 0 || ep.detailedRating.animation > 0 || ep.detailedRating.sound > 0)
    );
    if (ratedEpisodes.length === 0) return 0;
    
    return ratedEpisodes.reduce((sum, ep) => {
      const ratings = [ep.detailedRating.content, ep.detailedRating.animation, ep.detailedRating.sound].filter(r => r > 0);
      const epAvg = ratings.length > 0 ? ratings.reduce((a, b) => a + b) / ratings.length : 0;
      return sum + epAvg;
    }, 0) / ratedEpisodes.length;
  } else {
    const ratedEpisodes = anime.episodes.filter(ep => ep.rating > 0);
    return ratedEpisodes.length > 0 
      ? ratedEpisodes.reduce((sum, ep) => sum + ep.rating, 0) / ratedEpisodes.length 
      : 0;
  }
};

// アニメのランクを計算する関数
export const getAnimeRank = (anime, allRatings) => {
  const avgRating = getAnimeAverageRating(anime);
  return avgRating > 0 ? calculateRank(avgRating, allRatings) : null;
};

// 全評価値を取得する関数
export const getAllRatings = (animes) => {
  return animes.flatMap(anime => {
    if (anime.ratingMode === 'detailed') {
      return anime.episodes
        .filter(ep => ep.detailedRating && (ep.detailedRating.content > 0 || ep.detailedRating.animation > 0 || ep.detailedRating.sound > 0))
        .map(ep => {
          const ratings = [ep.detailedRating.content, ep.detailedRating.animation, ep.detailedRating.sound].filter(r => r > 0);
          return ratings.length > 0 ? ratings.reduce((a, b) => a + b) / ratings.length : 0;
        });
    } else {
      return anime.episodes.filter(ep => ep.rating > 0).map(ep => ep.rating);
    }
  }).filter(r => r > 0);
};

// エピソードの詳細評価の平均を計算する関数
export const getEpisodeDetailedRatingAverage = (episode) => {
  if (!episode.detailedRating) return 0;
  
  const ratings = [
    episode.detailedRating.content,
    episode.detailedRating.animation,
    episode.detailedRating.sound
  ].filter(r => r > 0);
  
  return ratings.length > 0 ? ratings.reduce((a, b) => a + b) / ratings.length : 0;
};

// 新しいアニメオブジェクトを作成する関数
export const createNewAnime = (formData) => {
  return {
    id: Date.now().toString(),
    title: formData.title,
    subtitle: formData.subtitle,
    year: parseInt(formData.year),
    season: formData.season,
    ratingMode: formData.ratingMode,
    startEpisode: formData.startEpisode || 1,
    dropped: false,
    droppedAtEpisode: null,
    episodes: Array.from({ length: formData.totalEpisodes }, (_, i) => ({
      id: `${Date.now()}-${i}`,
      number: (formData.startEpisode || 1) + i,
      title: `第${(formData.startEpisode || 1) + i}話`,
      storyTitle: '',
      rating: 0,
      detailedRating: {
        content: 0,
        animation: 0,
        sound: 0
      },
      notes: '',
      watchedDate: null
    })),
    thumbnail: null,
    favoriteCharacters: [],
    broadcastDay: '',
    streamingSite: '',
    studio: '',
    director: '',
    officialSite: '',
    pvUrl: '',
    officialX: '',
    opSongs: [{ title: '', artist: '', rating: 0 }],
    edSongs: [{ title: '', artist: '', rating: 0 }]
  };
};
